import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { isMovingTenantUser, MOVING_BACK_OFFICE_ROLES } from '../utils/movingTenant';
import { Truck, Loader2, Mail, Lock, User, Phone, Sun, Moon, AlertCircle, Globe, Eye, EyeOff, Store, Search, Wrench, Sparkles, ArrowRight, MapPin } from 'lucide-react';
import LanguageSelector from '../components/LanguageSelector';

const LoginPage = () => {
  const [identifier, setIdentifier] = useState(''); // Can be email or username
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState(null);
  const { login, loginWithGoogle } = useAuth();
  const { theme, toggleTheme, isDark } = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // iter 259 — A/B test: 3 headline variants for the marketplace promo. We
  // pick one at random on mount, fire an `impression` event once, and fire a
  // `click` event when the user taps the CTA. Super admin reviews CTR via
  // /api/marketplace/ad-stats.
  const adVariant = React.useMemo(() => {
    const variants = [
      {
        id: 'v1_phone_tag',
        title: 'Find equipment, mechanics & parking — without the phone tag.',
        sub: 'Drayly Marketplace connects you with verified providers across the West Coast. No broker fees.'
      },
      {
        id: 'v2_save_fees',
        title: 'Stop paying broker fees on chassis, parking & repair.',
        sub: 'Direct booking, in-app payments, instant confirmation. Keep more of every load.'
      },
      {
        id: 'v3_minutes',
        title: 'Need a chassis in 5 minutes? A mechanic in 15?',
        sub: 'Drayly Marketplace lists verified providers near every West Coast port. Pay in-app, done.'
      }
    ];
    return variants[Math.floor(Math.random() * variants.length)];
  }, []);

  React.useEffect(() => {
    // Fire impression once per visit
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL;
      fetch(`${apiBase}/api/marketplace/ad-event`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ variant_id: adVariant.id, event: 'impression' })
      }).catch(() => {});
    } catch (_) { /* fire-and-forget */ }
  }, [adVariant.id]);

  const handleAdClick = () => {
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL;
      // sendBeacon is more reliable across navigations than fetch
      const blob = new Blob([JSON.stringify({ variant_id: adVariant.id, event: 'click' })], { type: 'application/json' });
      if (navigator.sendBeacon) {
        navigator.sendBeacon(`${apiBase}/api/marketplace/ad-event`, blob);
      } else {
        fetch(`${apiBase}/api/marketplace/ad-event`, {
          method: 'POST', keepalive: true,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ variant_id: adVariant.id, event: 'click' })
        }).catch(() => {});
      }
    } catch (_) { /* fire-and-forget */ }
  };

  // Check for OAuth errors in URL on mount
  useEffect(() => {
    const hash = window.location.hash;
    const search = window.location.search;
    
    // Parse hash and search params for errors
    const hashParams = hash ? new URLSearchParams(hash.substring(1)) : new URLSearchParams();
    const searchParams = new URLSearchParams(search);
    
    const error = hashParams.get('error') || searchParams.get('error');
    const errorDescription = hashParams.get('error_description') || searchParams.get('error_description');
    
    if (error) {
      const errorMsg = `Google login failed: ${errorDescription || error}`;
      setAuthError(errorMsg);
      toast.error(errorMsg);
      // Clear the error from URL
      window.history.replaceState(null, '', '/login');
      return;
    }
    
    // Check for error stored in sessionStorage (from AuthCallback)
    const storedError = sessionStorage.getItem('authError');
    if (storedError) {
      try {
        const errorMsg = JSON.parse(storedError);
        setAuthError(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
        toast.error(typeof errorMsg === 'string' ? errorMsg : 'Authentication failed');
      } catch {
        setAuthError(storedError);
      }
      sessionStorage.removeItem('authError');
      return;
    }
    
    // Also check for state in localStorage that might indicate a failed auth attempt
    const lastAuthAttempt = localStorage.getItem('lastAuthAttempt');
    if (lastAuthAttempt) {
      const attemptTime = parseInt(lastAuthAttempt);
      const now = Date.now();
      // If there was an auth attempt in the last 30 seconds and we're back on login, show error
      if (now - attemptTime < 30000) {
        setAuthError('Google login did not complete. Please try again.');
      }
      localStorage.removeItem('lastAuthAttempt');
    }

    // Session-expired bounce from the global 401 interceptor
    if (searchParams.get('session') === 'expired') {
      toast.info('Your session expired. Please sign in again.');
      window.history.replaceState(null, '', '/login');
    }
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setAuthError(null);
    try {
      const userData = await login(identifier, password);
      toast.success('Welcome back!', { duration: 1500 });
      // Redirect based on role and approval status
      if (userData?.approval_status === 'pending') {
        navigate('/pending-approval');
      } else if (userData?.role === 'super_admin') {
        navigate('/super-admin');
      } else if (['marketplace_customer', 'marketplace_vendor'].includes(userData?.role)) {
        navigate('/marketplace');
      } else if (userData?.role === 'driver') {
        navigate('/driver');
      } else if (userData?.role === 'foreman') {
        // Crew (drivers + foremen) share one mobile app.
        navigate('/moving/closings/mobile');
      } else if (isMovingTenantUser(userData) && MOVING_BACK_OFFICE_ROLES.includes(userData?.role)) {
        // Moving-industry tenants land on /moving on every device (phones included).
        navigate('/moving');
      } else if ((userData?.role === 'dispatcher' || userData?.role === 'admin') && window.innerWidth <= 768) {
        navigate('/dispatch-mobile');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // Track that we're attempting Google login
    localStorage.setItem('lastAuthAttempt', Date.now().toString());
    setAuthError(null);
    loginWithGoogle();
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4 relative">
      {/* Theme & Language Toggle - top right corner */}
      <div className="absolute top-4 right-4 flex items-center gap-2">
        <LanguageSelector compact />
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          className="text-slate-400 hover:text-white hover:bg-slate-800"
          data-testid="login-theme-toggle-btn"
          title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </Button>
      </div>

      <div className="w-full max-w-5xl grid lg:grid-cols-[minmax(0,420px)_1fr] gap-10 lg:gap-16 items-center animate-fade-in">
        {/* ─── Login form column ─── */}
        <div className="w-full max-w-md mx-auto lg:mx-0">
        <div className="flex justify-center lg:justify-start mb-8">
          <div className="flex items-center gap-3">
            <img src="/logo192.png" alt="Drayly" className="w-14 h-14 object-contain rounded-xl bg-slate-900 p-0.5" />
            <div>
              <h1 className="font-heading font-bold text-2xl text-white">Drayly</h1>
              <p className="text-slate-500 text-sm">Trucker Dispatch System</p>
            </div>
          </div>
        </div>

        {/* Auth Error Display */}
        {authError && (
          <div className="mb-4 p-4 bg-red-900/50 border border-red-700 rounded-lg flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-200 text-sm font-medium">Authentication Error</p>
              <p className="text-red-300 text-sm mt-1">{authError}</p>
            </div>
          </div>
        )}

        <Card className="bg-slate-900 border-slate-800">
          <CardHeader className="space-y-1">
            <CardTitle className="font-heading text-xl text-white">{t('auth.signIn')}</CardTitle>
            <CardDescription className="text-slate-400">
              {t('auth.noAccount')} <Link to="/register" className="text-blue-400 hover:underline">{t('auth.signUp')}</Link>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="identifier" className="text-slate-300">{t('auth.usernameOrEmail')}</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input
                    id="identifier"
                    type="text"
                    placeholder={t('auth.usernameOrEmail').toLowerCase()}
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    className="pl-10 bg-slate-950 border-slate-800 text-white placeholder:text-slate-500"
                    required
                    data-testid="login-email-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-300">{t('auth.password')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 bg-slate-950 border-slate-800 text-white placeholder:text-slate-500"
                    required
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                    data-testid="login-toggle-password"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-500 text-white font-medium"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {t('auth.signIn')}
              </Button>
            </form>

            <div className="relative my-6">
              <Separator className="bg-slate-800" />
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-slate-900 px-2 text-xs text-slate-500">
                OR
              </span>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full border-slate-700 hover:bg-slate-800 text-white"
              onClick={handleGoogleLogin}
              data-testid="google-login-btn"
            >
              <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>

            <div className="text-center mt-4">
              <Link to="/forgot-password" className="text-sm text-slate-400 hover:text-blue-400">
                Forgot your password?
              </Link>
            </div>

            <p className="text-center text-sm text-slate-400 mt-6">
              {t('auth.noAccount')}{' '}
              <Link to="/register" className="text-blue-400 hover:text-blue-300 font-medium">
                {t('auth.signUp')}
              </Link>
            </p>
            <p className="text-center text-xs text-slate-500 mt-3">
              Looking for the Marketplace?{' '}
              <Link to="/marketplace/login" className="text-green-400 hover:text-green-300">
                Sign in here
              </Link>
            </p>
          </CardContent>
        </Card>
        </div>
        {/* ─── Marketplace ad column (hidden on mobile, visible lg+) ─── */}
        {/* Theme note: we use <div> (not <aside>/<h2>/<p>) so the global
            html.light overrides for those tags don't repaint this branded
            promo panel in light mode. Backgrounds use arbitrary inline styles
            to bypass html.light .bg-slate-9xx overrides as well. */}
        <div className="hidden lg:flex flex-col" data-testid="login-marketplace-ad">
          <div
            className="relative overflow-hidden rounded-2xl border border-emerald-700/30 p-8 shadow-2xl"
            style={{
              backgroundImage:
                'linear-gradient(135deg, #0f172a 0%, #0f172a 55%, rgba(6, 78, 59, 0.45) 100%)'
            }}
          >
            {/* decorative bottom orb only — top orb removed because its blur halo
                washed out the headline's white text at low contrast. */}
            <div className="absolute -bottom-16 -left-10 w-44 h-44 rounded-full bg-amber-500/10 blur-3xl pointer-events-none" />

            <div className="relative">
              <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-300 text-[11px] font-semibold tracking-wider mb-5">
                <Sparkles className="h-3 w-3" />
                MARKETPLACE
              </div>

              <div
                className="text-2xl xl:text-3xl font-bold leading-tight mb-3"
                style={{ color: '#ffffff' }}
                data-testid="login-marketplace-headline"
                data-variant={adVariant.id}
              >
                {adVariant.title}
              </div>
              <div className="text-sm mb-6" style={{ color: '#94a3b8' }}>
                {adVariant.sub}
              </div>

              <ul className="space-y-3 mb-7">
                <li className="flex items-start gap-3">
                  <div className="h-9 w-9 rounded-lg flex items-center justify-center text-emerald-400 flex-shrink-0" style={{ backgroundColor: 'rgba(30, 41, 59, 0.7)' }}>
                    <Search className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold" style={{ color: '#ffffff' }}>Chassis &amp; trailer rentals</div>
                    <div className="text-xs" style={{ color: '#64748b' }}>Daily, weekly, monthly — paid in-app</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="h-9 w-9 rounded-lg flex items-center justify-center text-amber-400 flex-shrink-0" style={{ backgroundColor: 'rgba(30, 41, 59, 0.7)' }}>
                    <Wrench className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold" style={{ color: '#ffffff' }}>On-demand mechanics</div>
                    <div className="text-xs" style={{ color: '#64748b' }}>GPS-matched, real-time arrival tracking</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="h-9 w-9 rounded-lg flex items-center justify-center text-cyan-400 flex-shrink-0" style={{ backgroundColor: 'rgba(30, 41, 59, 0.7)' }}>
                    <MapPin className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold" style={{ color: '#ffffff' }}>Yard &amp; overnight parking</div>
                    <div className="text-xs" style={{ color: '#64748b' }}>Secure, verified lots near every port</div>
                  </div>
                </li>
              </ul>

              <Link
                to="/marketplace"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 font-semibold text-sm transition-colors shadow-lg shadow-emerald-500/20"
                style={{ color: '#0f172a' }}
                onClick={handleAdClick}
                data-testid="login-marketplace-cta"
              >
                <Store className="h-4 w-4" />
                Browse the Marketplace
                <ArrowRight className="h-4 w-4" />
              </Link>
              <div className="text-[11px] mt-3" style={{ color: '#64748b' }}>Free to browse · No Drayly account required</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    role: 'dispatcher',
    phone: '',
    discount_code: '',
  });
  const [loading, setLoading] = useState(false);
  const [showRegPassword, setShowRegPassword] = useState(false);
  const [codeState, setCodeState] = useState({ status: 'idle', discount: null, reason: null });
  const { register, loginWithGoogle } = useAuth();
  const { theme, toggleTheme, isDark } = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (e.target.name === 'discount_code') {
      setCodeState({ status: 'idle', discount: null, reason: null });
    }
  };

  // iter 307.63 — live-validate the discount code without blocking submit
  React.useEffect(() => {
    const code = (formData.discount_code || '').trim();
    if (code.length < 3) return;
    const t = setTimeout(async () => {
      setCodeState({ status: 'checking', discount: null, reason: null });
      try {
        const { default: axios } = await import('axios');
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/collaborator/validate-code?code=${encodeURIComponent(code)}`;
        const r = await axios.get(url);
        if (r.data?.valid) {
          setCodeState({ status: 'valid', discount: r.data.discount, reason: null });
        } else {
          setCodeState({ status: 'invalid', discount: null, reason: r.data?.reason || 'not_found' });
        }
      } catch {
        setCodeState({ status: 'idle', discount: null, reason: null });
      }
    }, 400);
    return () => clearTimeout(t);
  }, [formData.discount_code]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await register(
        formData.email, formData.password, formData.name, formData.role,
        { phone: formData.phone, discount_code: formData.discount_code || undefined },
      );
      toast.success('Account created successfully!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4 relative">
      {/* Theme & Language Toggle - top right corner */}
      <div className="absolute top-4 right-4 flex items-center gap-2">
        <LanguageSelector compact />
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          className="text-slate-400 hover:text-white hover:bg-slate-800"
          data-testid="register-theme-toggle-btn"
          title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </Button>
      </div>

      <div className="w-full max-w-md animate-fade-in">
        <div className="flex justify-center mb-8">
          <div className="flex items-center gap-3">
            <img src="/logo192.png" alt="Drayly" className="w-14 h-14 object-contain rounded-xl bg-slate-900 p-0.5" />
            <div>
              <h1 className="font-heading font-bold text-2xl text-white">Drayly</h1>
              <p className="text-slate-500 text-sm">Trucker Dispatch System</p>
            </div>
          </div>
        </div>

        <Card className="bg-slate-900 border-slate-800">
          <CardHeader className="space-y-1">
            <CardTitle className="font-heading text-xl text-white">{t('auth.register')}</CardTitle>
            <CardDescription className="text-slate-400">
              Enter your details to create a new account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-slate-300">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={handleChange}
                    className="pl-10 bg-slate-950 border-slate-800 text-white placeholder:text-slate-500"
                    required
                    data-testid="register-name-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-300">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="dispatcher@company.com"
                    value={formData.email}
                    onChange={handleChange}
                    className="pl-10 bg-slate-950 border-slate-800 text-white placeholder:text-slate-500"
                    required
                    data-testid="register-email-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="role" className="text-slate-300">Role</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger className="bg-slate-950 border-slate-800 text-white" data-testid="register-role-select">
                    <SelectValue placeholder="Select a role" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    <SelectItem value="dispatcher" className="text-white hover:bg-slate-800">Dispatcher</SelectItem>
                    <SelectItem value="driver" className="text-white hover:bg-slate-800">Driver</SelectItem>
                    <SelectItem value="admin" className="text-white hover:bg-slate-800">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-slate-300">Phone (Optional)</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="+1 (555) 000-0000"
                    value={formData.phone}
                    onChange={handleChange}
                    className="pl-10 bg-slate-950 border-slate-800 text-white placeholder:text-slate-500"
                    data-testid="register-phone-input"
                  />
                </div>
              </div>

              {/* iter 307.63 — Optional collaborator / discount code */}
              <div className="space-y-2">
                <Label htmlFor="discount_code" className="text-slate-300 flex items-center gap-2">
                  Discount Code <span className="text-xs text-slate-500 font-normal">(optional)</span>
                </Label>
                <div className="relative">
                  <Sparkles className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input
                    id="discount_code"
                    name="discount_code"
                    type="text"
                    placeholder="FRIEND10"
                    value={formData.discount_code}
                    onChange={(e) => handleChange({ target: { name: 'discount_code', value: e.target.value.toUpperCase() } })}
                    className="pl-10 pr-28 bg-slate-950 border-slate-800 text-white placeholder:text-slate-500 uppercase tracking-wider"
                    data-testid="register-discount-code-input"
                    autoComplete="off"
                  />
                  {codeState.status === 'checking' && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500 flex items-center gap-1">
                      <Loader2 className="h-3 w-3 animate-spin" /> checking…
                    </span>
                  )}
                  {codeState.status === 'valid' && (
                    <span
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-green-400 font-medium"
                      data-testid="register-discount-code-valid"
                    >
                      ✓ {codeState.discount?.type === 'percent'
                            ? `${codeState.discount.value}% off`
                            : `$${codeState.discount?.value} off`}
                    </span>
                  )}
                  {codeState.status === 'invalid' && (
                    <span
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-red-400"
                      data-testid="register-discount-code-invalid"
                    >
                      {codeState.reason === 'expired' ? 'expired'
                        : codeState.reason === 'exhausted' ? 'exhausted'
                        : 'invalid'}
                    </span>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-300">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input
                    id="password"
                    name="password"
                    type={showRegPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    className="pl-10 pr-10 bg-slate-950 border-slate-800 text-white placeholder:text-slate-500"
                    required
                    data-testid="register-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowRegPassword(!showRegPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                    data-testid="register-toggle-password"
                    tabIndex={-1}
                  >
                    {showRegPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-slate-300">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showRegPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="pl-10 pr-10 bg-slate-950 border-slate-800 text-white placeholder:text-slate-500"
                    required
                    data-testid="register-confirm-password-input"
                  />
                </div>
              </div>
              <Button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-500 text-white font-medium"
                disabled={loading}
                data-testid="register-submit-btn"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Create Account
              </Button>
            </form>

            <div className="relative my-6">
              <Separator className="bg-slate-800" />
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-slate-900 px-2 text-xs text-slate-500">
                OR
              </span>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full border-slate-700 hover:bg-slate-800 text-white"
              onClick={loginWithGoogle}
              data-testid="google-register-btn"
            >
              <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>

            <p className="text-center text-sm text-slate-400 mt-6">
              Already have an account?{' '}
              <Link to="/login" className="text-blue-400 hover:text-blue-300 font-medium">
                Sign in
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export { LoginPage, RegisterPage };
